import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboard } from '@/context/DashboardContext';
import { PROGRAM_LIST } from '@/data/seed';
import { TRANCHES } from '@/data/pipeline';
import { cr, crFull, fmtDate, daysFrom, utilColor } from '@/lib/fmt';
import { Chip, ProgramChip, ProgressBar } from '@/components/UiBits';
import { FILTERS, GRANT } from '@/constants/testIds';
import { RotateCcw, Pencil, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const empty = {
  donor: '', name: '', program: 'education', sanct: 1, recd: 0, util: 0,
  end: '2027-03-31', report: '2026-12-31', uc: 'Submitted', health: 'On Track',
  benef: 0, states: ['Delhi NCR'], fy: 'FY 25-26',
};

const trOverdue   = (t) => t.status === 'To Invoice' && daysFrom(t.due) < 0;
const trFundsLate = (t) => t.status === 'Invoiced' && daysFrom(t.due) < 0;

function TrancheChip({ t }) {
  if (t.status === 'In Bank')  return <Chip kind="On Track">In bank</Chip>;
  if (t.status === 'Invoiced') return trFundsLate(t) ? <Chip kind="At Risk">Invoiced · funds late</Chip> : <Chip kind="New">Invoiced · awaiting funds</Chip>;
  return trOverdue(t) ? <Chip kind="Overdue">Invoice overdue</Chip> : <Chip kind="Due">To invoice</Chip>;
}

function TrancheAge({ t }) {
  const d = daysFrom(t.due);
  if (t.status === 'In Bank')  return <span style={{ color: 'var(--gray-500)' }}>Received {fmtDate(t.recdOn)}</span>;
  if (t.status === 'Invoiced') return d < 0 ? <span style={{ color: 'var(--red-600)', fontWeight: 600 }}>{-d}d past expected ⚠</span> : <span>Funds expected in {d}d</span>;
  return d < 0 ? <span style={{ color: 'var(--red-600)', fontWeight: 600 }}>{-d}d late — raise now ⚠</span> : <span>Invoice due in {d}d</span>;
}

export default function Grants() {
  const { donors, grants, addGrant, updateGrant, removeGrant, PROGRAMS: P, REGIONS, FYS } = useDashboard();
  const [pw, setPw] = useState('');
  const [health, setHealth] = useState('');
  const [uc, setUc] = useState('');
  const [q, setQ] = useState('');
  const [tf, setTf] = useState('');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const active = [pw, health, uc, q].filter(Boolean).length;
  const reset = () => { setPw(''); setHealth(''); setUc(''); setQ(''); };

  const donorById = (id) => donors.find((d) => d.id === id);
  const list = grants.filter((g) =>
    (!pw || g.program === pw) &&
    (!health || g.health === health) &&
    (!uc || g.uc === uc) &&
    (!q || (g.name + ' ' + (donorById(g.donor)?.name || '')).toLowerCase().includes(q.toLowerCase()))
  );

  const openAdd  = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (g) => { setEditingId(g.id); setForm({ ...g }); setOpen(true); };
  const save = () => {
    const sanct = parseFloat(form.sanct);
    const recd  = parseFloat(form.recd);
    const util  = parseFloat(form.util);
    if (!form.name.trim()) return toast.error('Grant name is required');
    if (!form.donor)       return toast.error('Donor is required');
    if (Number.isNaN(sanct) || sanct <= 0) return toast.error('Enter a valid sanctioned amount');
    if (Number.isNaN(recd)  || recd  < 0)  return toast.error('Enter a valid received amount');
    if (Number.isNaN(util)  || util  < 0)  return toast.error('Enter a valid utilised amount');
    if (util > recd) return toast.error('Utilised cannot exceed received');
    if (recd > sanct) return toast.error('Received cannot exceed sanctioned');
    const payload = { ...form, sanct, recd, util, benef: parseInt(form.benef, 10) || 0 };
    if (editingId) { updateGrant(editingId, payload); toast.success('Grant updated'); }
    else { addGrant(payload); toast.success('Grant added'); }
    setOpen(false);
  };
  const del = (g) => { removeGrant(g.id); toast.success('Grant removed'); };

  // Tranche filtered set
  const trList = TRANCHES.filter((t) => !tf ||
    (tf === '__overdue' ? trOverdue(t) :
     tf === '__toinv'   ? (t.status === 'To Invoice' && !trOverdue(t)) :
     t.status === tf));
  const trRank = (t) => trOverdue(t) ? 0 : t.status === 'To Invoice' ? 1 : t.status === 'Invoiced' ? 2 : 3;
  const trSorted = [...trList].sort((a, b) => trRank(a) - trRank(b) || (trRank(a) === 3
    ? new Date(b.recdOn) - new Date(a.recdOn)
    : new Date(a.due) - new Date(b.due)));

  const ov  = TRANCHES.filter(trOverdue);
  const toI = TRANCHES.filter((t) => t.status === 'To Invoice' && !trOverdue(t));
  const aw  = TRANCHES.filter((t) => t.status === 'Invoiced');
  const bk  = TRANCHES.filter((t) => t.status === 'In Bank');

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Money layer</div>
          <h1>Grants &amp; Utilisation</h1>
          <div className="sub">Grant-wise commitment, receipt, spend and donor obligations — the flip side of the MoU</div>
        </div>
        <Button
          type="button"
          data-testid={GRANT.add}
          onClick={openAdd}
          className="gap-2 rounded-lg font-medium"
          style={{ background: 'var(--llf-ink)', color: 'var(--llf-gold)' }}
        >
          <Plus size={14} /> New grant
        </Button>
      </div>

      <div className="filter-strip">
        <div className="filter-group">
          <label>Program</label>
          <select data-testid={FILTERS.gf.program} value={pw} onChange={(e) => setPw(e.target.value)}>
            <option value="">All programs</option>
            {PROGRAM_LIST.map((k) => <option key={k} value={k}>{P[k].name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Health</label>
          <select data-testid={FILTERS.gf.health} value={health} onChange={(e) => setHealth(e.target.value)}>
            <option value="">All</option>
            {['On Track','Watch','At Risk'].map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>UC Status</label>
          <select data-testid={FILTERS.gf.uc} value={uc} onChange={(e) => setUc(e.target.value)}>
            <option value="">All</option>
            {['Submitted','Due','Overdue'].map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input data-testid={FILTERS.gf.search} type="search" placeholder="Search grant or donor…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {active > 0 && (
          <button className="btn-reset" data-testid={FILTERS.gf.reset} onClick={reset} style={{ display: 'inline-flex' }}>
            <RotateCcw size={13} /> Reset Filters <span className="badge-count">{active}</span>
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Tranche &amp; Invoicing Tracker</div>
            <div className="card-sub">
              {trList.length} of {TRANCHES.length} tranches · receivables for next 2 quarters + last 60 days receipts
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={tf} onChange={(e) => setTf(e.target.value)}
              className="h-8 px-2 border border-[color:var(--gray-200)] rounded-lg text-[12px] text-[color:var(--gray-700)]">
              <option value="">All statuses</option>
              <option value="__overdue">Invoice overdue</option>
              <option value="__toinv">To invoice</option>
              <option value="Invoiced">Invoiced</option>
              <option value="In Bank">In bank</option>
            </select>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-[12.5px] text-[color:var(--gray-500)] mb-3">
          <span>Invoice overdue: <b style={{ color: 'var(--red-600)' }}>{ov.length} · {cr(ov.reduce((s, t) => s + t.amt, 0))}</b></span>
          <span>To invoice (next 90d): <b style={{ color: 'var(--amber-600)' }}>{toI.length} · {cr(toI.reduce((s, t) => s + t.amt, 0))}</b></span>
          <span>Invoiced, awaiting funds: <b style={{ color: 'var(--blue-700)' }}>{aw.length} · {cr(aw.reduce((s, t) => s + t.amt, 0))}</b></span>
          <span>In bank (last 60d): <b style={{ color: 'var(--green-600)' }}>{bk.length} · {cr(bk.reduce((s, t) => s + t.amt, 0))}</b></span>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Grant</th><th className="ctr">Tranche</th><th className="num">Amount</th><th>Due</th><th className="ctr">Status</th><th>Notes</th></tr>
            </thead>
            <tbody>
              {trSorted.map((t, i) => {
                const g = grants.find((x) => x.id === t.grant);
                if (!g) return null;
                const d = donorById(g.donor);
                return (
                  <tr key={i}>
                    <td><span className="ent">{g.name}</span><span className="sub">{d?.name}{d?.fcra ? ' · FCRA' : ''}</span></td>
                    <td className="ctr" style={{ whiteSpace: 'nowrap' }}>{t.t}</td>
                    <td className="num" title={crFull(t.amt)}>{cr(t.amt)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.due)}</td>
                    <td className="ctr"><TrancheChip t={t} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}><TrancheAge t={t} /></td>
                  </tr>
                );
              })}
              {trSorted.length === 0 && (
                <tr><td colSpan={6}>
                  <div className="empty-state"><div className="empty-title">No tranches match</div><div className="empty-sub">Clear the status filter.</div></div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Grant Register</div>
            <div className="card-sub">
              {list.length} of {grants.length} grants · sanction · utilisation · health · UC status
            </div>
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Grant ID</th><th>Grant &middot; Donor</th><th>Program</th><th className="num">Sanct.</th><th className="num">Recd.</th>
                <th>Utilisation</th><th className="ctr">Health</th><th>Reporting</th><th className="ctr">UC</th><th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((g) => {
                const d = donorById(g.donor);
                const pct = g.recd ? (g.util / g.recd) * 100 : 0;
                const dd = daysFrom(g.report);
                return (
                  <tr key={g.id} data-testid={GRANT.row(g.id)}>
                    <td>
                      <Link to={`/grants/${g.id}`} className="font-mono text-[11.5px] font-semibold text-[color:var(--llf-gold-text)] tracking-wider hover:underline"
                        data-testid={`grant-id-link-${g.id}`}>
                        {g.id}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/grants/${g.id}`} className="ent hover:text-[color:var(--llf-gold-text)] transition-colors" style={{ fontWeight: 700 }}
                        data-testid={`grant-name-link-${g.id}`}>
                        {g.name}
                      </Link>
                      <span className="sub">{d?.name}{d?.fcra ? ' · FCRA' : ''}</span>
                    </td>
                    <td><ProgramChip program={g.program} PROGRAMS={P} /></td>
                    <td className="num" title={crFull(g.sanct)}>{cr(g.sanct)}</td>
                    <td className="num" title={crFull(g.recd)}>{cr(g.recd)}</td>
                    <td><ProgressBar pct={pct} color={utilColor(pct)} /></td>
                    <td className="ctr"><Chip kind={g.health}>{g.health}</Chip></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {fmtDate(g.report)}
                      <span className="text-[11px] ml-1" style={{ color: dd < 0 ? 'var(--red-600)' : 'var(--gray-400)' }}>
                        ({dd < 0 ? Math.abs(dd) + 'd overdue' : 'in ' + dd + 'd'})
                      </span>
                    </td>
                    <td className="ctr"><Chip kind={g.uc}>{g.uc}</Chip></td>
                    <td>
                      <div className="flex gap-1 opacity-80 hover:opacity-100">
                        <button data-testid={GRANT.edit(g.id)} onClick={() => openEdit(g)} aria-label="Edit grant"
                          className="w-7 h-7 grid place-items-center rounded border border-[color:var(--gray-200)] bg-white hover:bg-[color:var(--gray-100)]">
                          <Pencil size={12} />
                        </button>
                        <button data-testid={GRANT.delete(g.id)} onClick={() => del(g)} aria-label="Delete grant"
                          className="w-7 h-7 grid place-items-center rounded border border-[color:var(--gray-200)] bg-white hover:bg-[color:var(--red-100)] hover:text-[color:var(--red-700)]">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={10}>
                  <div className="empty-state"><div className="empty-title">No grants match filters</div><div className="empty-sub">Clear a filter to see all grants.</div></div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[color:var(--gray-900)]">
              {editingId ? 'Edit grant' : 'New grant'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Grant name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mission Ed. Karnataka" />
            </Field>
            <Field label="Donor">
              <Select value={form.donor} onValueChange={(v) => setForm({ ...form, donor: v })}>
                <SelectTrigger data-testid={GRANT.donor}><SelectValue placeholder="Select donor" /></SelectTrigger>
                <SelectContent>
                  {donors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Program">
              <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                <SelectTrigger data-testid={GRANT.program}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROGRAM_LIST.map((k) => <SelectItem key={k} value={k}>{P[k].name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Fiscal year">
              <Select value={form.fy} onValueChange={(v) => setForm({ ...form, fy: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FYS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Sanctioned (₹ Cr)">
              <Input data-testid={GRANT.amount} type="number" step="0.1" value={form.sanct}
                onChange={(e) => setForm({ ...form, sanct: e.target.value })} />
            </Field>
            <Field label="Received (₹ Cr)">
              <Input type="number" step="0.1" value={form.recd}
                onChange={(e) => setForm({ ...form, recd: e.target.value })} />
            </Field>
            <Field label="Utilised (₹ Cr)">
              <Input data-testid={GRANT.util} type="number" step="0.01" value={form.util}
                onChange={(e) => setForm({ ...form, util: e.target.value })} />
            </Field>
            <Field label="Beneficiaries">
              <Input type="number" value={form.benef}
                onChange={(e) => setForm({ ...form, benef: e.target.value })} />
            </Field>
            <Field label="Reporting due">
              <Input data-testid={GRANT.report} type="date" value={form.report}
                onChange={(e) => setForm({ ...form, report: e.target.value })} />
            </Field>
            <Field label="Grant end date">
              <Input type="date" value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })} />
            </Field>
            <Field label="UC status">
              <Select value={form.uc} onValueChange={(v) => setForm({ ...form, uc: v })}>
                <SelectTrigger data-testid={GRANT.status}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Submitted','Due','Overdue'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Health">
              <Select value={form.health} onValueChange={(v) => setForm({ ...form, health: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['On Track','Watch','At Risk'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Primary state / region">
              <Select value={form.states[0]} onValueChange={(v) => setForm({ ...form, states: [v] })}>
                <SelectTrigger data-testid={GRANT.region}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" data-testid={GRANT.cancel} onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={GRANT.save} onClick={save}
              style={{ background: 'var(--llf-ink)', color: 'var(--llf-gold)' }}>
              {editingId ? 'Save grant' : 'Add grant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-[color:var(--gray-500)] font-semibold">{label}</Label>
    {children}
  </div>
);
